import { SearchInput } from '@/ui/components/search-input'
import {
    Filter,
    StyledHeader,
    FilterLabelsContainer,
} from './SearchFilterSection.styles'
import { memo } from 'react'
import { FilterButton } from '../buttons/FilterButton'
import { FilterLabel } from '../FilterLabel'
import { FilterPopup } from '../FilterPopup'
import { useSearchFilterSection } from './useSearchFilterSection'
import { TGenericFilterType } from '@core/modules/filters'
import { TextButton } from '../buttons/textButton'

export type CombinedFilterType = TGenericFilterType

export interface FilterItem {
    id: string
    name: string
}

export interface ActiveFilter {
    filterName: string
    filters: FilterItem[]
}

interface FilterConfig {
    [key: string]: {
        label: string
    }
}

interface FilterOptions {
    options: any[]
    activeOptions: any[]
    withSearch?: boolean
}

export interface SearchFilterSectionProps<T> {
    allItems: T[]
    filteredItems: T[]
    onSearchChange: (searchKey: string) => void
    onFilterChange: (type: CombinedFilterType, selectedOptions: any) => void
    getFilterOptions: (filterType: any) => FilterOptions
    filterConfig: FilterConfig | any
    searchValue?: string
    activeFilters?: ActiveFilter[]
    onFilterRemove?: (filterData: {
        filterName: string
        filters: FilterItem[]
    }) => void
    onFilterClick?: (filterData: {
        filterName: string
        filters: FilterItem[]
    }) => void
    onFilterReset?: () => void
    searchPlaceholder?: string
    itemLabel?: string
    showFilterPopup?: boolean
    searchInputWidth?: string
}

const SearchFilterSection = memo(
    <T,>({
        allItems = [],
        filteredItems = [],
        onSearchChange,
        onFilterChange,
        searchValue = '',
        filterConfig,
        getFilterOptions,
        activeFilters: externalActiveFilters,
        onFilterRemove,
        onFilterClick,
        onFilterReset,
        searchPlaceholder = 'Search',
        itemLabel = 'items',
        showFilterPopup = true,
        searchInputWidth,
    }: SearchFilterSectionProps<T>) => {
        const {
            localSearch,
            isFilterPopupOpen,
            filterButtonRef,
            activeFilters,
            filterSections,
            hasActiveFilters,
            handleFilterApply,
            handleFilterRemove,
            handleFilterClick,
            handleFilterReset,
            handleFilterButtonClick,
            handleFilterPopupClose,
            handleSearchChange,
        } = useSearchFilterSection({
            onSearchChange,
            onFilterChange,
            searchValue,
            filterConfig,
            getFilterOptions,
            activeFilters: externalActiveFilters,
            onFilterRemove,
            onFilterClick,
            onFilterReset,
            showFilterPopup,
        })

        return (
            <StyledHeader>
                <SearchInput
                    onChange={handleSearchChange}
                    value={localSearch}
                    placeholder={searchPlaceholder}
                    width={searchInputWidth}
                />
                <Filter>
                    <FilterPopup
                        sections={filterSections}
                        onApply={handleFilterApply}
                        onCancel={handleFilterPopupClose}
                        isOpen={isFilterPopupOpen}
                        onToggle={handleFilterButtonClick}
                        activatorRef={filterButtonRef}
                    >
                        <FilterButton
                            ref={filterButtonRef}
                            label="Filter"
                            active={hasActiveFilters}
                            onClick={handleFilterButtonClick}
                            filteredCount={filteredItems.length}
                            totalCount={allItems.length}
                            statusLabel={itemLabel}
                            emptyStatusText={`0 ${itemLabel}`}
                            hasActiveFilters={hasActiveFilters}
                        />
                    </FilterPopup>
                </Filter>
                {hasActiveFilters && (
                    <FilterLabelsContainer>
                        {activeFilters.map((filter) => (
                            <FilterLabel
                                key={filter.filterName}
                                filterName={filter.filterName}
                                filters={filter.filters}
                                onRemove={handleFilterRemove}
                                onClick={handleFilterClick}
                            />
                        ))}
                        {hasActiveFilters && (
                            <TextButton
                                text="Clear Filters"
                                dataTestId="clear-filters-button"
                                onClick={handleFilterReset}
                                padding="0"
                            />
                        )}
                    </FilterLabelsContainer>
                )}
            </StyledHeader>
        )
    }
)

export default SearchFilterSection
